import { blobToBase64 } from "@/common/utils/blob-serialization";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";

const KEY_PREFIX = "offline-verbal-confirmation-";

export type OfflineVerbalConfirmation = {
	shiftId: number;
	recordingId: string;
	base64: string;
	contentType: string;
	sizeBytes: number;
	storedAt: string;
};

export function useOfflineVerbalConfirmationCache() {
	const cache = useCacheStorage();

	const getKey = (shiftId: number) => `${KEY_PREFIX}${shiftId}`;

	const get = async (
		shiftId: number,
	): Promise<OfflineVerbalConfirmation | null> => {
		const raw = await cache.getItem(getKey(shiftId));
		if (!raw) return null;
		try {
			return JSON.parse(raw) as OfflineVerbalConfirmation;
		} catch {
			await cache.removeItem(getKey(shiftId));
			return null;
		}
	};

	const set = async (
		shiftId: number,
		blob: Blob,
		recordingId: string,
	): Promise<void> => {
		const base64 = await blobToBase64(blob);
		const data: OfflineVerbalConfirmation = {
			shiftId,
			recordingId,
			base64,
			contentType: blob.type,
			sizeBytes: blob.size,
			storedAt: new Date().toISOString(),
		};
		await cache.setItem(getKey(shiftId), JSON.stringify(data));
	};

	const remove = async (shiftId: number): Promise<void> => {
		await cache.removeItem(getKey(shiftId));
	};

	return { get, set, remove };
}
